import { IDebugEvent, TDebugEventType } from '../types';
import { generateUniqueId } from '../utils';

// React를 조건부로 import (선택적 의존성)
let React: any;
try {
  React = require('react');
} catch (e) {
  // React가 없는 환경에서는 무시
  React = undefined;
}

/**
 * 에러 감지 시스템의 설정 인터페이스
 */
export interface IErrorDetectorConfig {
  captureConsole?: boolean;
  captureUnhandledErrors?: boolean;
  captureUnhandledRejections?: boolean;
  captureReactErrors?: boolean;
  includeSourceMap?: boolean;
  maxStackFrames?: number;
  excludePatterns?: RegExp[];
  onError?: (event: IDebugEvent) => void;
}

/**
 * 에러 컨텍스트 정보
 */
export interface IErrorContextLocal {
  url: string;
  userAgent: string;
  timestamp: number;
  userId?: string;
  sessionId?: string;
  reactState?: Record<string, unknown>;
  reactProps?: Record<string, unknown>;
  domSnapshot?: string;
  breadcrumbs?: IBreadcrumbLocal[];
  originalError?: Error | string;
  performance?: any;
  network?: any;
  stateSnapshot?: any;
}

/**
 * 브레드크럼 (사용자 행동 추적)
 */
export interface IBreadcrumbLocal {
  id: string;
  type: 'navigation' | 'user_action' | 'http' | 'console' | 'state_change';
  message: string;
  data?: Record<string, unknown>;
  timestamp: number;
  level: 'info' | 'warning' | 'error';
}

/**
 * 에러 상세 정보
 */
export interface IErrorDetails {
  id: string;
  type: TDebugEventType;
  message: string;
  stack?: string;
  filename?: string;
  lineNumber?: number;
  columnNumber?: number;
  source?: 'javascript' | 'promise' | 'console' | 'react' | 'manual';
  level: 'info' | 'warning' | 'error' | 'critical';
  context: IErrorContextLocal;
  originalError?: Error | string;
}

/**
 * 완전한 에러 감지 시스템
 *
 * JavaScript 에러, Promise rejection, console 메시지, React 에러 등을
 * 포괄적으로 감지하고 컨텍스트 정보와 함께 수집합니다.
 */
export class ErrorDetector {
  private _config: Required<Omit<IErrorDetectorConfig, 'onError'>> & {
    onError?: (event: IDebugEvent) => void;
  };
  private _isActive: boolean = false;
  private _breadcrumbs: IBreadcrumbLocal[] = [];
  private _maxBreadcrumbs: number = 100;

  // 원본 함수들 저장
  private _originalConsole: Console;
  private _originalOnError: OnErrorEventHandler | null;
  private _originalOnUnhandledRejection: ((event: PromiseRejectionEvent) => void) | null;

  // React Error Boundary 지원
  private _reactErrorHandler?: (error: Error, errorInfo: any) => void;

  constructor(config: Partial<IErrorDetectorConfig> = {}) {
    this._config = {
      captureConsole: true,
      captureUnhandledErrors: true,
      captureUnhandledRejections: true,
      captureReactErrors: true,
      includeSourceMap: false, // 기본적으로 비활성화 (성능상 이유)
      maxStackFrames: 50,
      excludePatterns: [
        /chrome-extension:/,
        /moz-extension:/,
        /webkit-masked-url:/,
        /^Script error\.?$/,
        /Non-Error promise rejection captured/,
      ],
      onError: undefined,
      ...config,
    };

    // 원본 함수들 저장
    this._originalConsole = { ...console };
    this._originalOnError = window.onerror;
    this._originalOnUnhandledRejection = window.onunhandledrejection;
  }

  /**
   * 에러 감지 시작
   */
  public start(): void {
    if (this._isActive) {
      console.warn('[ErrorDetector] Already active');
      return;
    }

    console.log('[ErrorDetector] Starting error detection...');

    if (this._config.captureUnhandledErrors) {
      this._setupGlobalErrorHandler();
    }

    if (this._config.captureUnhandledRejections) {
      this._setupUnhandledRejectionHandler();
    }

    if (this._config.captureConsole) {
      this._setupConsoleOverride();
    }

    if (this._config.captureReactErrors) {
      this._setupReactErrorHandler();
    }

    this._isActive = true;
    console.log('[ErrorDetector] Error detection started successfully');
  }

  /**
   * 에러 감지 중단
   */
  public stop(): void {
    if (!this._isActive) {
      return;
    }

    console.log('[ErrorDetector] Stopping error detection...');

    // 원본 함수들 복원
    window.onerror = this._originalOnError;
    window.onunhandledrejection = this._originalOnUnhandledRejection;

    // Console 복원
    Object.assign(console, this._originalConsole);

    this._isActive = false;
    console.log('[ErrorDetector] Error detection stopped');
  }

  /**
   * 브레드크럼 추가
   */
  public addBreadcrumb(breadcrumb: Omit<IBreadcrumbLocal, 'id' | 'timestamp'>): void {
    const newBreadcrumb: IBreadcrumbLocal = {
      id: generateUniqueId(),
      timestamp: Date.now(),
      ...breadcrumb,
    };

    this._breadcrumbs.push(newBreadcrumb);

    // 최대 개수 제한
    if (this._breadcrumbs.length > this._maxBreadcrumbs) {
      this._breadcrumbs = this._breadcrumbs.slice(-this._maxBreadcrumbs);
    }
  }

  /**
   * React Error Handler 설정
   */
  public setReactErrorHandler(handler: (error: Error, errorInfo: any) => void): void {
    this._reactErrorHandler = handler;
  }

  /**
   * 현재 컨텍스트 정보 수집
   */
  public getCurrentContext(): IErrorContextLocal {
    return {
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      timestamp: Date.now(),
      domSnapshot: this._captureDOMSnapshot(),
      breadcrumbs: [...this._breadcrumbs],
    };
  }

  /**
   * 글로벌 JavaScript 에러 핸들러 설정
   */
  private _setupGlobalErrorHandler(): void {
    window.onerror = (message, filename, lineNumber, columnNumber, error) => {
      // 제외 패턴 확인
      if (this._shouldExcludeError(message.toString())) {
        return this._originalOnError?.(message, filename, lineNumber, columnNumber, error);
      }

      const errorDetails: IErrorDetails = {
        id: generateUniqueId(),
        type: 'error',
        message: message.toString(),
        stack: error?.stack,
        filename,
        lineNumber,
        columnNumber,
        source: 'javascript',
        level: 'error',
        context: this._enhanceContext({
          originalError: error,
        }),
        originalError: error,
      };

      this._handleError(errorDetails);

      // 브레드크럼 추가
      this.addBreadcrumb({
        type: 'console',
        message: `JavaScript Error: ${message}`,
        level: 'error',
        data: { filename, lineNumber, columnNumber },
      });

      // 원본 핸들러도 호출
      return this._originalOnError?.(message, filename, lineNumber, columnNumber, error);
    };
  }

  /**
   * Unhandled Promise Rejection 핸들러 설정
   */
  private _setupUnhandledRejectionHandler(): void {
    window.onunhandledrejection = (event: PromiseRejectionEvent) => {
      const rejectionReason = event.reason;
      const message =
        rejectionReason instanceof Error ? rejectionReason.message : String(rejectionReason);

      if (this._shouldExcludeError(message)) {
        return this._originalOnUnhandledRejection?.(event);
      }

      const errorDetails: IErrorDetails = {
        id: generateUniqueId(),
        type: 'error',
        message: `Unhandled Promise Rejection: ${message}`,
        stack: rejectionReason instanceof Error ? rejectionReason.stack : undefined,
        source: 'promise',
        level: 'error',
        context: this._enhanceContext({
          originalError: rejectionReason,
        }),
        originalError: rejectionReason,
      };

      this._handleError(errorDetails);

      // 브레드크럼 추가
      this.addBreadcrumb({
        type: 'console',
        message: `Promise Rejection: ${message}`,
        level: 'error',
        data: { reason: String(rejectionReason) },
      });

      // 원본 핸들러도 호출
      return this._originalOnUnhandledRejection?.(event);
    };
  }

  /**
   * Console 메서드 오버라이드
   */
  private _setupConsoleOverride(): void {
    const consoleMethodsToOverride = ['log', 'info', 'warn', 'error', 'debug'] as const;

    consoleMethodsToOverride.forEach(methodName => {
      const originalMethod = this._originalConsole[methodName];

      (console as any)[methodName] = (...args: any[]) => {
        // 원본 console 메서드 먼저 호출
        originalMethod.apply(console, args);

        // 메시지 조합
        const message = args
          .map(arg => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
          .join(' ');

        // 로그 레벨 결정
        const level = methodName === 'error' ? 'error' : methodName === 'warn' ? 'warning' : 'info';

        const errorDetails: IErrorDetails = {
          id: generateUniqueId(),
          type: methodName === 'error' ? 'error' : 'info',
          message: `Console.${methodName}: ${message}`,
          source: 'console',
          level,
          context: this.getCurrentContext(),
        };

        this._handleError(errorDetails);

        // 브레드크럼 추가
        this.addBreadcrumb({
          type: 'console',
          message: `Console.${methodName}: ${message.substring(0, 100)}`,
          level,
          data: { method: methodName, args },
        });
      };
    });
  }

  /**
   * React Error Handler 설정
   */
  private _setupReactErrorHandler(): void {
    // React Error Boundary를 위한 글로벌 핸들러
    if (typeof window !== 'undefined') {
      window.addEventListener('react-error', (event: any) => {
        this._handleReactError(event.detail.error, event.detail.errorInfo);
      });
    }
  }

  /**
   * React 에러 처리
   */
  private _handleReactError(error: Error, errorInfo: any): void {
    const errorDetails: IErrorDetails = {
      id: generateUniqueId(),
      type: 'error',
      message: `React Error: ${error.message}`,
      stack: error.stack,
      source: 'react',
      level: 'critical',
      context: this._enhanceContext({
        reactState: errorInfo.componentStack,
        reactProps: errorInfo.props,
        originalError: error,
      }),
      originalError: error,
    };

    this._handleError(errorDetails);

    // 브레드크럼 추가
    this.addBreadcrumb({
      type: 'console',
      message: `React Error in component: ${error.message}`,
      level: 'error',
      data: {
        componentStack: errorInfo.componentStack,
        errorBoundary: errorInfo.errorBoundary,
      },
    });

    // React Error Handler 호출
    this._reactErrorHandler?.(error, errorInfo);
  }

  /**
   * 에러 제외 여부 확인
   */
  private _shouldExcludeError(message: string): boolean {
    return this._config.excludePatterns.some(pattern => pattern.test(message));
  }

  /**
   * 컨텍스트 정보 강화
   */
  private _enhanceContext(additionalContext: Partial<IErrorContextLocal> = {}): IErrorContextLocal {
    const baseContext = this.getCurrentContext();
    return {
      ...baseContext,
      ...additionalContext,
    };
  }

  /**
   * DOM 스냅샷 캡처 (간단한 버전)
   */
  private _captureDOMSnapshot(): string {
    if (typeof document === 'undefined') {
      return '';
    }

    try {
      // 현재 활성 요소와 기본 DOM 정보만 캡처 (성능 고려)
      const activeElement = document.activeElement;
      const snapshot = {
        activeElement: activeElement
          ? {
              tagName: activeElement.tagName,
              id: activeElement.id,
              className: activeElement.className,
              textContent: activeElement.textContent?.substring(0, 100),
            }
          : null,
        url: window.location.href,
        title: document.title,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      };

      return JSON.stringify(snapshot);
    } catch (error) {
      return `Error capturing DOM snapshot: ${error}`;
    }
  }

  /**
   * 에러 이벤트 처리
   */
  private _handleError(errorDetails: IErrorDetails): void {
    // 스택 추적 정리
    if (errorDetails.stack && this._config.maxStackFrames > 0) {
      const stackLines = errorDetails.stack.split('\n');
      if (stackLines.length > this._config.maxStackFrames) {
        errorDetails.stack = stackLines.slice(0, this._config.maxStackFrames).join('\n');
      }
    }

    // Debug Event로 변환
    const debugEvent: IDebugEvent = {
      id: errorDetails.id,
      type: errorDetails.type,
      data: errorDetails,
      timestamp: Date.now(),
      source: 'ErrorDetector',
    };

    // 설정된 콜백 호출 (안전하게)
    if (this._config.onError && typeof this._config.onError === 'function') {
      this._config.onError(debugEvent);
    }
  }

  /**
   * 현재 상태 가져오기
   */
  public getState(): {
    isActive: boolean;
    breadcrumbsCount: number;
    config: Required<Omit<IErrorDetectorConfig, 'onError'>> & {
      onError?: (event: IDebugEvent) => void;
    };
  } {
    return {
      isActive: this._isActive,
      breadcrumbsCount: this._breadcrumbs.length,
      config: { ...this._config },
    };
  }

  /**
   * 설정 업데이트
   */
  public updateConfig(newConfig: Partial<IErrorDetectorConfig>): void {
    const wasActive = this._isActive;

    if (wasActive) {
      this.stop();
    }

    this._config = { ...this._config, ...newConfig };

    if (wasActive) {
      this.start();
    }
  }
}

/**
 * 글로벌 ErrorDetector 인스턴스
 */
let globalErrorDetector: ErrorDetector | null = null;

/**
 * 글로벌 ErrorDetector 가져오기/생성
 */
export function getGlobalErrorDetector(config?: Partial<IErrorDetectorConfig>): ErrorDetector {
  if (!globalErrorDetector) {
    globalErrorDetector = new ErrorDetector(config);
  }
  return globalErrorDetector;
}

/**
 * React Error Boundary HOC
 */
export function createReactErrorBoundary(errorDetector: ErrorDetector) {
  // React가 사용 가능한 환경에서만 실행
  if (typeof React === 'undefined') {
    console.warn('[ErrorDetector] React is not available, skipping Error Boundary creation');
    return null;
  }

  return class TimeMachineErrorBoundary extends React.Component<any, any> {
    constructor(props: any) {
      super(props);
      this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error) {
      return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: any) {
      // React 에러를 ErrorDetector에 전달
      if (errorDetector) {
        errorDetector.addBreadcrumb({
          type: 'console',
          message: `React Error in component: ${error.message}`,
          level: 'error',
          data: {
            componentStack: errorInfo.componentStack,
            error: {
              name: error.name,
              message: error.message,
              stack: error.stack,
            },
          },
        });
      }

      // Custom event 발생
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('react-error', {
            detail: { error, errorInfo },
          })
        );
      }
    }

    render() {
      if (this.state.hasError) {
        return React.createElement(
          'div',
          {
            style: { padding: '20px', color: 'red' },
          },
          'Something went wrong.'
        );
      }

      return this.props.children;
    }
  };
}
